import { useEffect, useState } from 'react';

/**
 * Open-state for a hover popup that also closes as soon as anything scrolls.
 *
 * A hover popup normally closes on `mouseleave`. That is not enough inside a
 * scrollable panel: when the pointer is held still and the content moves
 * underneath it, the browser may not deliver `mouseleave` until the scroll
 * finishes — momentum scrolling on a trackpad makes this plainly visible, the
 * popup hanging in place long after its trigger has moved away.
 *
 * Listening for scroll in the capture phase catches it from any ancestor, since
 * scroll events do not bubble.
 */
const useDismissOnScroll = (): [boolean, (open: boolean) => void] => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    const close = () => setIsOpen(false);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [isOpen]);

  return [isOpen, setIsOpen];
};

export default useDismissOnScroll;
